import React, { useState, ChangeEvent, SyntheticEvent, useEffect } from 'react';
import { Grid, Typography, FormControlLabel, TextField, FormHelperTextProps, Checkbox } from '@material-ui/core';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useLayoutContext } from '../../../Context/Layout.context';
import {
  CalendarEventTypeId,
  MessageTypeId,
  ProcedureDto,
  ProcedureExecutionTypeId,
  ProcedureTypeId,
  RecipientType,
} from '../../../pages/Calendar/Calendar.types';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { useStyles } from './AddProcedure.styles';
import {
  getProcedureAddressee,
  getProcedureNotificationTypeLabel,
  getProcedureRecipientLabel,
  getProcedureStartTimeLabel,
  getProcedureStartTypeLabel,
} from '../helpers';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import { t } from 'i18next';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import HideSourceIcon from '@mui/icons-material/HideSource';
import { CloseIcon } from '../../Solution/Menu/CloseIcon.component';
import {
  addAssessmentSessionEventProcedure,
  getAssessmentSessionEventProcedure,
  updateAssessmentSessionEventProcedure,
} from '../../../services/exams/assessmentSession/assessments.service';

const radioAndCheckboxStyle = {
  color: '#3C419A',
  '&.Mui-checked': {
    color: '#3C419A',
  },
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon sx={radioAndCheckboxStyle} fontSize="small" />;

const ChipProps = {
  deleteIcon: <CloseIcon style={{ width: '18px' }} />,
  style: { color: '#2C2D3E', background: '#FAFAFF', border: '1px solid rgba(44, 45, 62, 0.3', height: '25px' },
};

const PROCEDURE_NAME_MAX_LENGTH = 150;
const BEFORE_START_MIN_TIME = 1;
const BEFORE_START_MAX_TIME = 24;
const MESSAGE_TITLE_MAX_LENGTH = 150;
const MESSAGE_MAX_LENGTH = 256;
const SMS_MAX_LENGTH = 160;

interface AddProcedureProps {
  data?: ProcedureDto;
  calendarEventTypeId: CalendarEventTypeId;
  assessmentSessionId: string;
  isEdit: boolean;
  onClose: () => void;
}

const enum ErrorCode {
  WrongProcedureExecutionTimeProvided = 'Wrong procedure execution time provided',
  WrongTimeSelected = 'Wrong time selected',
}

export const AddProcedure: React.FC<AddProcedureProps> = ({
  data,
  calendarEventTypeId,
  assessmentSessionId,
  isEdit,
  onClose,
}) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();

  const [procedureName, setProcedureName] = useState<string>((data && data.name) ?? '');
  const [procedureTypeId, setProcedureTypeId] = useState<ProcedureTypeId | null>(
    (data && data.procedureTypeId) ?? null,
  );
  const [selectedProcedureRecipients, setSelectedProcedureRecipients] = useState<RecipientType[]>(
    data && data.procedureAddressee ? getProcedureAddressee(data.procedureAddressee) : [],
  );
  const [procedureExecutionTypeId, setProcedureExecutionTypeId] = useState<ProcedureExecutionTypeId | null>(
    (data && data.procedureExecutionTypeId) ?? null,
  );
  const [plannedExecutionAdditionalHours, setPlannedExecutionAdditionalHours] = useState<number | null>(
    (data && data.plannedExecutionAdditionalHours) ?? null,
  );
  const [messageTypeId, setMessageTypeId] = useState<MessageTypeId | null>((data && data.messageTypeId) ?? null);
  const [oneMessageTitle, setOneMessageTitle] = useState<string>('');
  const [emailMessageTitle, setEmailMessageTitle] = useState<string>('');
  const [oneMessageContent, setOneMessageContent] = useState<string>('');
  const [pushMessageContent, setPushMessageContent] = useState<string>('');
  const [emailMessageContent, setEmailMessageContent] = useState<string>('');
  const [smsMessageContent, setSmsMessageContent] = useState<string>('');
  const [hasProcedureNameFocus, setHasProcedureNameFocus] = useState<boolean>(false);
  const [hasMessageSubjectFocus, setHasMessageSubjectFocus] = useState<boolean>(false);
  const [hasMessageFocus, setHasMessageFocus] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<ErrorCode | undefined>();

  const isProcedureNameToLong = procedureName.length > PROCEDURE_NAME_MAX_LENGTH;
  const isOneMessageTitleToLong = oneMessageTitle.length > MESSAGE_TITLE_MAX_LENGTH;
  const isOneMessageToLong = oneMessageContent.length > MESSAGE_MAX_LENGTH;
  const isPushMessageToLong = pushMessageContent.length > MESSAGE_MAX_LENGTH;
  const isEmailMessageToLong = emailMessageContent.length > MESSAGE_MAX_LENGTH;
  const isEmailMessageTitleToLong = emailMessageTitle.length > MESSAGE_TITLE_MAX_LENGTH;
  const isSmsMessageToLong = smsMessageContent.length > SMS_MAX_LENGTH;

  const getProcedureAddresseeForApiRequest = () => {
    return {
      admin: selectedProcedureRecipients.some((e) => e === RecipientType.admin),
      helpdesk: selectedProcedureRecipients.some((e) => e === RecipientType.helpdesk),
      okeAdmin: selectedProcedureRecipients.some((e) => e === RecipientType.okeAdmin),
      domesticCoordinator: selectedProcedureRecipients.some((e) => e === RecipientType.domesticCoordinator),
      examTaskGroupCoordinator: selectedProcedureRecipients.some((e) => e === RecipientType.examTaskGroupCoordinator),
      examiningTeamChairman: selectedProcedureRecipients.some((e) => e === RecipientType.examiningTeamChairman),
      examiner: selectedProcedureRecipients.some((e) => e === RecipientType.examiner),
    };
  };

  const handleGetProcedure = () => {
    showLoader(true);
    if (data && data.id) {
      getAssessmentSessionEventProcedure(data.id)
        .then(
          (response) => {
            if (response.data) {
              setPlannedExecutionAdditionalHours(response.data.plannedExecutionAdditionalHours);
              setMessageTypeId(response.data.message ? response.data.message.messageTypeId : null);
              if (response.data.message) {
                const messageTitle = response.data.message.title;
                const messageContent = response.data.message.content;
                const messageId = response.data.message.messageTypeId;
                if (messageTitle) {
                  switch (messageId) {
                    case MessageTypeId.one:
                      setOneMessageTitle(messageTitle);
                      break;
                    case MessageTypeId.email:
                      setEmailMessageTitle(messageTitle);
                      break;
                  }
                }
                if (messageContent) {
                  switch (messageId) {
                    case MessageTypeId.push:
                      setPushMessageContent(messageContent);
                      break;
                    case MessageTypeId.sms:
                      setSmsMessageContent(messageContent);
                      break;
                    case MessageTypeId.one:
                      setOneMessageContent(messageContent);
                      break;
                    case MessageTypeId.email:
                      setEmailMessageContent(messageContent);
                  }
                }
              }
              if (response.data.plannedExecutionAdditionalHours) {
                setPlannedExecutionAdditionalHours(Math.abs(response.data.plannedExecutionAdditionalHours));
              }
            }
          },
          () => {
            openAlert('get_error');
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  useEffect(() => {
    if (data) {
      handleGetProcedure();
    }
  }, [data]);

  const getAddProcedureRequestBody = () => {
    return {
      name: procedureName,
      procedureTypeId: procedureTypeId,
      procedureExecutionTypeId: procedureExecutionTypeId,
      procedureAddressee: getProcedureAddresseeForApiRequest(),
      message: {
        title: oneMessageTitle ? oneMessageTitle : emailMessageTitle ? emailMessageTitle : null,
        content: pushMessageContent
          ? pushMessageContent
          : oneMessageContent
          ? oneMessageContent
          : emailMessageContent
          ? emailMessageContent
          : smsMessageContent,
        messageTypeId: messageTypeId,
      },
      plannedExecutionAdditionalHours: plannedExecutionAdditionalHours,
      calendarEventTypeId: calendarEventTypeId,
      assessmentSessionId: assessmentSessionId,
      executed: (data && data.executed) ?? false,
    };
  };

  const handleAddProcedure = () => {
    showLoader(true);
    addAssessmentSessionEventProcedure(getAddProcedureRequestBody())
      .then(
        () => {
          openAlert('success', 'Procedura została pomyślnie dodana');
          onClose();
          setErrorCode(undefined);
        },
        (error) => {
          if (!navigator.onLine) {
            openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
          } else if (error.response.data === ErrorCode.WrongTimeSelected) {
            setErrorCode(error.response.data);
          } else openAlert('error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleUpdateProcedure = () => {
    showLoader(true);
    if (data && data.id) {
      updateAssessmentSessionEventProcedure(getAddProcedureRequestBody(), data.id)
        .then(
          () => {
            openAlert('success', 'Procedura została pomyślnie dodana');
            onClose();
            setErrorCode(undefined);
          },
          (error) => {
            if (!navigator.onLine) {
              openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            } else if (error.response.data === ErrorCode.WrongTimeSelected) {
              setErrorCode(error.response.data);
            } else openAlert('error');
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  const isConfirmationButtonDisabled = () => {
    return (
      procedureName === '' ||
      procedureTypeId === null ||
      !selectedProcedureRecipients.length ||
      messageTypeId === null ||
      isProcedureNameToLong ||
      isOneMessageTitleToLong ||
      isOneMessageToLong ||
      isPushMessageToLong ||
      isEmailMessageTitleToLong ||
      isEmailMessageToLong ||
      isSmsMessageToLong
    );
  };

  const procedureStartTimeDisabled = () => {
    return procedureTypeId === ProcedureTypeId.manual || procedureTypeId === null;
  };

  const handleMessageTypeChange = (event: { target: { value: string | number } }) => {
    setMessageTypeId(+event.target.value);
    setOneMessageTitle('');
    setOneMessageContent('');
    setPushMessageContent('');
    setEmailMessageTitle('');
    setEmailMessageContent('');
    setSmsMessageContent('');
    setHasMessageFocus(false);
    setHasMessageSubjectFocus(false);
  };

  const handleProcedureNameChange = (value: string) => {
    setProcedureName(value);
  };

  const handleOneMessageTitleChange = (value: string) => {
    setOneMessageTitle(value);
  };

  const handleOneMessageContentChange = (value: string) => {
    setOneMessageContent(value);
  };

  const handlePushMessageContentChange = (value: string) => {
    setPushMessageContent(value);
  };

  const handleEmailMessageTitleChange = (value: string) => {
    setEmailMessageTitle(value);
  };

  const handleEmailMessageContentChange = (value: string) => {
    setEmailMessageContent(value);
  };

  const handleSmsMessageContentChange = (value: string) => {
    setSmsMessageContent(value);
  };

  const handlePlannedExecutionAdditionalHoursChange = (value: string) => {
    if (parseInt(value) < 1 || parseInt(value) > 24) return null;
    setPlannedExecutionAdditionalHours(parseInt(value));
  };

  const handleProcedureStartTypeChange = (event: { target: { value: string | number } }) => {
    setProcedureTypeId(+event.target.value);
    setProcedureExecutionTypeId(null);
    setPlannedExecutionAdditionalHours(null);
  };

  const handleRecipientsChange = (event: SyntheticEvent<Element, Event>, value: RecipientType[]) => {
    setSelectedProcedureRecipients(value);
  };

  const procedureRecipients = [
    RecipientType.examiner,
    RecipientType.examiningTeamChairman,
    RecipientType.examTaskGroupCoordinator,
    RecipientType.domesticCoordinator,
    RecipientType.admin,
    RecipientType.okeAdmin,
    RecipientType.helpdesk,
  ];

  return (
    <>
      <ModalWithButtons
        xCloseButton={onClose}
        open={true}
        size="sm"
        title={isEdit ? 'Edytuj procedurę' : 'Dodaj procedurę'}
        autoHeight
        confirmButtonLabel="Zapisz"
        onConfirm={isEdit ? handleUpdateProcedure : handleAddProcedure}
        confirmButtonDisabled={isConfirmationButtonDisabled()}
        onCancel={onClose}
        handleClose={onClose}
      >
        <Grid item xs={12}>
          <Typography className={classes.subTitle}>Wpisz nazwę procedury</Typography>
        </Grid>
        <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
          <TextField
            value={procedureName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleProcedureNameChange(e.target.value)}
            label="Nazwa procedury"
            variant="outlined"
            fullWidth
            inputProps={{
              style: {
                color: isProcedureNameToLong ? 'red' : '#2C2D3E',
              },
            }}
            onFocus={() => setHasProcedureNameFocus(true)}
            onBlur={() => setHasProcedureNameFocus(false)}
            error={isProcedureNameToLong}
            FormHelperTextProps={
              {
                component: 'div',
              } as Partial<FormHelperTextProps<'p'>>
            }
            helperText={
              <ValidationRow
                helperText={isProcedureNameToLong ? 'Wprowadzona nazwa jest za długa' : ''}
                limitsVisible={hasProcedureNameFocus || isProcedureNameToLong}
                valueLength={procedureName.length}
                limit={PROCEDURE_NAME_MAX_LENGTH}
                withError={isProcedureNameToLong}
              />
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.subTitle}>Wprowadź sposób uruchomienia procedury</Typography>
        </Grid>

        <Grid item xs={12} className={[classes.item, classes.itemWithRadioGroup].join(' ')}>
          <RadioGroup
            row
            aria-label="wybór sposobu uruchomienia procedury"
            name="procedure-start-type-selector"
            value={procedureTypeId}
            onChange={handleProcedureStartTypeChange}
          >
            <FormControlLabel
              value={ProcedureTypeId.manual}
              control={<Radio sx={radioAndCheckboxStyle} />}
              label={
                <Typography className={classes.label}>{getProcedureStartTypeLabel(ProcedureTypeId.manual)}</Typography>
              }
            />
            <FormControlLabel
              value={ProcedureTypeId.automatic}
              control={<Radio sx={radioAndCheckboxStyle} />}
              label={
                <Typography className={classes.label} style={{ alignItems: 'center' }}>
                  {getProcedureStartTypeLabel(ProcedureTypeId.automatic)}
                </Typography>
              }
            />
          </RadioGroup>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.subTitle}>Wybierz odbiorców procedury</Typography>
        </Grid>
        <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
          <Autocomplete
            multiple
            fullWidth
            id="checkboxes-tags-demo"
            options={procedureRecipients}
            value={selectedProcedureRecipients}
            disableCloseOnSelect
            getOptionLabel={(option) => getProcedureRecipientLabel(option)}
            onChange={handleRecipientsChange}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ height: '35px' }}>
                <Checkbox disableRipple icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                {getProcedureRecipientLabel(option)}
              </li>
            )}
            renderInput={(params) => <TextField variant="outlined" {...params} label="Wybierz z listy" />}
            ChipProps={ChipProps}
          />
        </Grid>

        {!procedureStartTimeDisabled() ? (
          <Grid item xs={12}>
            <Typography className={classes.subTitle} style={{ height: '25px', marginTop: '20px' }}>
              Wybierz czas uruchomienia procedury
            </Typography>
          </Grid>
        ) : (
          <Grid item direction="row" xs={12} className={[classes.subTitle, classes.subTitleDisabled].join(' ')}>
            <HideSourceIcon
              fontSize="small"
              style={{ marginTop: '3px', marginRight: '5px', color: 'rgba(44, 45, 62, 0.8)' }}
            />
            <Typography className={classes.subTitle} style={{ color: 'rgba(44, 45, 62, 0.8)' }}>
              Wybierz czas uruchomienia procedury
            </Typography>
          </Grid>
        )}

        <Grid item xs={12} className={[classes.item, classes.itemWithRadioGroup].join(' ')}>
          <RadioGroup
            row
            aria-label="wybór czasu uruchomienia procedury"
            name="procedure-start-time-selector"
            value={procedureExecutionTypeId}
            onChange={(e) => {
              setProcedureExecutionTypeId(+e.target.value);
            }}
          >
            <FormControlLabel
              value={ProcedureExecutionTypeId.beforeEventStartDate}
              control={<Radio sx={radioAndCheckboxStyle} />}
              disabled={procedureStartTimeDisabled()}
              label={
                <Typography className={classes.label} style={{ maxWidth: '80px', position: 'relative', top: '20px' }}>
                  {getProcedureStartTimeLabel(ProcedureExecutionTypeId.beforeEventStartDate)}
                </Typography>
              }
            />
            <FormControlLabel
              value={ProcedureExecutionTypeId.atEventStartDate}
              control={<Radio sx={radioAndCheckboxStyle} />}
              disabled={procedureStartTimeDisabled()}
              label={
                <Typography className={classes.label} style={{ maxWidth: '80px', position: 'relative', top: '10px' }}>
                  {getProcedureStartTimeLabel(ProcedureExecutionTypeId.atEventStartDate)}
                </Typography>
              }
            />
            <FormControlLabel
              value={ProcedureExecutionTypeId.atEventEndDate}
              control={<Radio sx={radioAndCheckboxStyle} />}
              disabled={procedureStartTimeDisabled()}
              label={
                <Typography className={classes.label} style={{ maxWidth: '80px', position: 'relative', top: '10px' }}>
                  {getProcedureStartTimeLabel(ProcedureExecutionTypeId.atEventEndDate)}
                </Typography>
              }
            />
            <FormControlLabel
              value={ProcedureExecutionTypeId.beforeEventEndDate}
              control={<Radio sx={radioAndCheckboxStyle} />}
              disabled={procedureStartTimeDisabled()}
              label={
                <Typography className={classes.label} style={{ maxWidth: '80px', position: 'relative', top: '20px' }}>
                  {getProcedureStartTimeLabel(ProcedureExecutionTypeId.beforeEventEndDate)}
                </Typography>
              }
            />
          </RadioGroup>
        </Grid>

        {(procedureExecutionTypeId === ProcedureExecutionTypeId.beforeEventStartDate ||
          procedureExecutionTypeId === ProcedureExecutionTypeId.beforeEventEndDate) && (
          <>
            <Typography className={classes.note}>
              (Określ, na ile godzin przed terminem rozpoczęcia lub zakończenia zdarzenia ma zostać uruchomiona
              procedura)
            </Typography>
            <Grid item xs={4} className={[classes.item, classes.itemTextField].join(' ')} style={{ marginTop: '10px' }}>
              <TextField
                value={plannedExecutionAdditionalHours}
                onChange={(event) => handlePlannedExecutionAdditionalHoursChange(event.target.value)}
                id="beforeStartTime"
                label="Liczba godzin"
                variant="outlined"
                type="number"
                fullWidth
                error={errorCode === ErrorCode.WrongTimeSelected}
                inputProps={{
                  style: {
                    padding: '15px',
                    color: '#2C2D3E',
                  },
                  min: BEFORE_START_MIN_TIME,
                  max: BEFORE_START_MAX_TIME,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                FormHelperTextProps={{
                  style: {
                    margin: '5px 0px 0px 0px',
                    fontSize: '11px',
                  },
                }}
                helperText={errorCode === ErrorCode.WrongTimeSelected ? 'Nieprawidłowa liczba godzin' : undefined}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Typography className={classes.subTitle} style={{ marginTop: '30px' }}>
            Wybierz typ powiadomienia dla procedury
          </Typography>
        </Grid>
        <Grid item xs={12} className={[classes.item, classes.itemWithRadioGroup].join(' ')}>
          <RadioGroup
            row
            aria-label="wybór typu powiadomienia dla procedury"
            name="procedure-notification-type-selector"
            value={messageTypeId}
            onChange={handleMessageTypeChange}
          >
            <FormControlLabel
              value={MessageTypeId.push}
              control={<Radio sx={radioAndCheckboxStyle} />}
              label={
                <Typography className={classes.label} style={{ maxWidth: '125px' }}>
                  {getProcedureNotificationTypeLabel(MessageTypeId.push)}
                </Typography>
              }
            />
            <FormControlLabel
              value={MessageTypeId.one}
              control={<Radio sx={radioAndCheckboxStyle} />}
              label={
                <Typography className={classes.label} style={{ maxWidth: '145px' }}>
                  {getProcedureNotificationTypeLabel(MessageTypeId.one)}
                </Typography>
              }
            />
            <FormControlLabel
              value={MessageTypeId.email}
              control={<Radio sx={radioAndCheckboxStyle} />}
              label={
                <Typography className={classes.label} style={{ maxWidth: '40px' }}>
                  {getProcedureNotificationTypeLabel(MessageTypeId.email)}
                </Typography>
              }
            />
            <FormControlLabel
              value={MessageTypeId.sms}
              control={<Radio sx={radioAndCheckboxStyle} />}
              label={
                <Typography className={classes.label} style={{ maxWidth: '20px' }}>
                  {getProcedureNotificationTypeLabel(MessageTypeId.sms)}
                </Typography>
              }
            />
          </RadioGroup>
        </Grid>

        {messageTypeId === MessageTypeId.push && (
          <>
            <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
              <TextField
                value={pushMessageContent}
                multiline
                onChange={(e: ChangeEvent<HTMLInputElement>) => handlePushMessageContentChange(e.target.value)}
                label="Treść powiadomienia"
                variant="outlined"
                fullWidth
                inputProps={{
                  style: {
                    color: isPushMessageToLong ? 'red' : '#2C2D3E',
                    height: '60px',
                  },
                }}
                onFocus={() => setHasMessageFocus(true)}
                onBlur={() => setHasMessageFocus(false)}
                error={isPushMessageToLong}
                FormHelperTextProps={
                  {
                    component: 'div',
                  } as Partial<FormHelperTextProps<'p'>>
                }
                helperText={
                  <ValidationRow
                    helperText={isPushMessageToLong ? 'Wprowadzona treść jest za długa' : ''}
                    limitsVisible={hasMessageFocus || isPushMessageToLong}
                    valueLength={pushMessageContent.length}
                    limit={MESSAGE_MAX_LENGTH}
                    withError={isPushMessageToLong}
                  />
                }
              />
            </Grid>
          </>
        )}

        {messageTypeId === MessageTypeId.one && (
          <>
            <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
              <TextField
                value={oneMessageTitle}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleOneMessageTitleChange(e.target.value)}
                label="Temat wiadomości"
                variant="outlined"
                fullWidth
                inputProps={{
                  style: {
                    color: isOneMessageTitleToLong ? 'red' : '#2C2D3E',
                  },
                }}
                onFocus={() => setHasMessageSubjectFocus(true)}
                onBlur={() => setHasMessageSubjectFocus(false)}
                error={isOneMessageTitleToLong}
                FormHelperTextProps={
                  {
                    component: 'div',
                  } as Partial<FormHelperTextProps<'p'>>
                }
                helperText={
                  <ValidationRow
                    helperText={isOneMessageTitleToLong ? 'Wprowadzony temat jest za długi' : ''}
                    limitsVisible={hasMessageSubjectFocus || isOneMessageTitleToLong}
                    valueLength={oneMessageTitle.length}
                    limit={MESSAGE_TITLE_MAX_LENGTH}
                    withError={isOneMessageTitleToLong}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
              <TextField
                value={oneMessageContent}
                multiline
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleOneMessageContentChange(e.target.value)}
                label="Treść wiadomości"
                variant="outlined"
                fullWidth
                inputProps={{
                  style: {
                    color: isOneMessageToLong ? 'red' : '#2C2D3E',
                    height: '60px',
                  },
                }}
                onFocus={() => setHasMessageFocus(true)}
                onBlur={() => setHasMessageFocus(false)}
                error={isOneMessageToLong}
                FormHelperTextProps={
                  {
                    component: 'div',
                  } as Partial<FormHelperTextProps<'p'>>
                }
                helperText={
                  <ValidationRow
                    helperText={isOneMessageToLong ? 'Wprowadzona treść jest za długa' : ''}
                    limitsVisible={hasMessageFocus || isOneMessageToLong}
                    valueLength={oneMessageContent.length}
                    limit={MESSAGE_MAX_LENGTH}
                    withError={isOneMessageToLong}
                  />
                }
              />
            </Grid>
          </>
        )}

        {messageTypeId === MessageTypeId.email && (
          <>
            <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
              <TextField
                value={emailMessageTitle}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleEmailMessageTitleChange(e.target.value)}
                label="Temat wiadomości e-mail"
                variant="outlined"
                fullWidth
                inputProps={{
                  style: {
                    color: isEmailMessageTitleToLong ? 'red' : '#2C2D3E',
                  },
                }}
                onFocus={() => setHasMessageSubjectFocus(true)}
                onBlur={() => setHasMessageSubjectFocus(false)}
                error={isEmailMessageTitleToLong}
                FormHelperTextProps={
                  {
                    component: 'div',
                  } as Partial<FormHelperTextProps<'p'>>
                }
                helperText={
                  <ValidationRow
                    helperText={isEmailMessageTitleToLong ? 'Wprowadzony temat jest za długi' : ''}
                    limitsVisible={hasMessageSubjectFocus || isEmailMessageTitleToLong}
                    valueLength={emailMessageTitle.length}
                    limit={MESSAGE_TITLE_MAX_LENGTH}
                    withError={isEmailMessageTitleToLong}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
              <TextField
                value={emailMessageContent}
                multiline
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleEmailMessageContentChange(e.target.value)}
                label="Treść wiadomości e-mail"
                variant="outlined"
                fullWidth
                inputProps={{
                  style: {
                    color: isEmailMessageToLong ? 'red' : '#2C2D3E',
                    height: '60px',
                  },
                }}
                onFocus={() => setHasMessageFocus(true)}
                onBlur={() => setHasMessageFocus(false)}
                error={isEmailMessageToLong}
                FormHelperTextProps={
                  {
                    component: 'div',
                  } as Partial<FormHelperTextProps<'p'>>
                }
                helperText={
                  <ValidationRow
                    helperText={isEmailMessageToLong ? 'Wprowadzona treść jest za długa' : ''}
                    limitsVisible={hasMessageFocus || isEmailMessageToLong}
                    valueLength={emailMessageContent.length}
                    limit={MESSAGE_MAX_LENGTH}
                    withError={isEmailMessageToLong}
                  />
                }
              />
            </Grid>
          </>
        )}

        {messageTypeId === MessageTypeId.sms && (
          <>
            <Grid item xs={12} className={[classes.item, classes.itemTextField].join(' ')}>
              <TextField
                value={smsMessageContent}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleSmsMessageContentChange(e.target.value)}
                label="Treść sms"
                variant="outlined"
                fullWidth
                inputProps={{
                  style: {
                    color: isSmsMessageToLong ? 'red' : '#2C2D3E',
                  },
                }}
                onFocus={() => setHasMessageFocus(true)}
                onBlur={() => setHasMessageFocus(false)}
                error={isSmsMessageToLong}
                FormHelperTextProps={
                  {
                    component: 'div',
                  } as Partial<FormHelperTextProps<'p'>>
                }
                helperText={
                  <ValidationRow
                    helperText={isSmsMessageToLong ? 'Wprowadzona treść jest za długa' : ''}
                    limitsVisible={hasMessageFocus || isSmsMessageToLong}
                    valueLength={smsMessageContent.length}
                    limit={SMS_MAX_LENGTH}
                    withError={isSmsMessageToLong}
                  />
                }
              />
            </Grid>
          </>
        )}
      </ModalWithButtons>
    </>
  );
};
